// ─── alignerPlan.js ──────────────────────────────────────────────────
// Recalculer un plan d'analyses apres correction.
//
// Sert quand la demande d'analyses reellement envoyee ne correspond pas au
// plan enregistre. Anissa aligne, et tout ce qui depend des analyses doit
// suivre d'un bloc : le cout, les frais de labo, et surtout le montant a
// refacturer a la cliente.
//
// ⚠ CE FICHIER TOUCHE A DE L'ARGENT AVANCE. Anissa paie le labo puis
// refacture ; une ligne oubliee ici est une perte seche pour elle, et
// invisible, puisque personne ne recompte apres coup.

import { computeLabFees } from './labFees';
import { calculerCoutFacture } from './coutAnalyses';
import { PACK_DEFINITIONS } from './packSystem';
import { nombreDeBonsPour } from './clinical/catalog/orthoBonsDeCommande';

/** Le catalogue des bons ne connait qu'Ortho ; on ne lui demande rien d'autre. */
const estimateurDeBons = (lab, codes) => (
  lab === 'ortho_analytic' ? nombreDeBonsPour(codes) : null
);

/**
 * Reconstruit les champs monetaires d'un plan a partir d'une liste de codes.
 *
 * @param {{codes: string[], plan: object, catalogue: Array<object>}} entree
 * @returns {{
 *   ok: boolean,
 *   raison?: string,
 *   inconnus?: string[],
 *   champs?: object,
 * }}
 */
export function alignerPlan({ codes = [], plan = null, catalogue = [] } = {}) {
  if (!plan) return { ok: false, raison: 'plan_absent' };

  const parCode = new Map();
  for (const t of Array.isArray(catalogue) ? catalogue : []) {
    if (t?.code) parCode.set(String(t.code), t);
  }

  const voulus = [...new Set((Array.isArray(codes) ? codes : []).filter(Boolean).map(String))];
  const inconnus = voulus.filter(c => !parCode.has(c));
  // ⚠ Fail-closed. Un code absent du catalogue n'a pas de prix : continuer
  // produirait un total qui a l'air juste et qui manque une analyse. On
  // refuse, et l'appelant dit a Anissa ce qu'il faut ajouter au catalogue.
  if (inconnus.length > 0) return { ok: false, raison: 'codes_inconnus', inconnus };
  if (voulus.length === 0) return { ok: false, raison: 'aucune_analyse' };

  const tests = voulus.map(c => parCode.get(c));
  const frais = computeLabFees(tests, { sheetEstimator: estimateurDeBons });
  const credit = PACK_DEFINITIONS[plan.pack_type]?.includedAnalysisCreditChf || 0;
  // totalFrais et non totalTax : le second oublie le prelevement sanguin.
  const montants = calculerCoutFacture(tests, frais.totalFrais, credit);

  return {
    ok: true,
    champs: {
      selected_tests: tests.map(t => ({
        code: t.code,
        name: t.display_name,
        cost_chf: t.cost_anissa_chf,
        source_lab: t.source_lab,
        // La provenance compte : ces lignes ne viennent pas d'une
        // suggestion mais du formulaire que le labo a recu.
        status: 'confirme_par_demande',
      })),
      total_cost_anissa_chf: montants.totalCost,
      cost_for_client_chf: montants.costForClient,
      credit_applied_chf: montants.creditApplied,
      lab_fees: {
        labs: frais.labs.map(l => ({
          lab: l.lab,
          taxUnit: l.taxUnit,
          sheetCount: l.sheetCount,
          estimatedSheets: l.estimatedSheets,
          isOverridden: l.isOverridden,
          tax: l.tax,
          prelevement: l.prelevement,
          specimens: l.specimens,
        })),
        totalTax: frais.totalTax,
        totalPrelevement: frais.totalPrelevement,
        totalFrais: frais.totalFrais,
        totalSheets: frais.totalSheets,
        taxedSheets: frais.taxedSheets,
      },
      // Champ deprecate, tenu a jour pour ne pas laisser une valeur
      // incoherente derriere nous. Ne sert a aucune decision.
      total_margin_chf: (Number(plan.pack_price_chf) || 0) - montants.totalCost,
    },
  };
}

/** Ce qu'on montre a Anissa quand l'alignement est refuse. */
export function messageRefus(resultat) {
  if (!resultat || resultat.ok) return null;
  switch (resultat.raison) {
    case 'codes_inconnus':
      return `Je ne peux pas chiffrer ${resultat.inconnus.join(', ')} : `
        + 'ces analyses ne sont pas au catalogue. Ajoute-les avant d’aligner, '
        + 'sinon le montant à refacturer serait faux.';
    case 'aucune_analyse':
      return 'Un plan sans analyse ne veut rien dire. Supprime-le plutôt.';
    case 'plan_absent':
      return 'Aucun plan à aligner.';
    default:
      return 'L’alignement a échoué.';
  }
}
